import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ModulePageHeading from '../../components/ModulePageHeading';
import { queueBadgeClass, resolvePaperworkNav } from '../../navigation/paperworkNavigation';
import api from '../../utils/api';
import { formatSsLabel } from '../../utils/formatSsLabel';

function formatWhen(v) {
  if (!v) return '';
  const s = String(v);
  return s.slice(0, 16).replace('T', ' ');
}

function eventLabel(type) {
  const t = String(type || '');
  if (t === 'take') return 'Take';
  if (t === 'cancel' || t === 'release') return 'Cancel';
  if (t === 'close') return 'Confirm / close';
  if (t === 'note') return 'Note';
  return t || 'Event';
}

export default function AdminPaperworkTaskDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [docId, setDocId] = useState('');
  const logOpen = searchParams.get('log') === '1';

  const setLogOpen = (open) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set('log', '1');
    else next.delete('log');
    setSearchParams(next, { replace: true });
  };

  const { data: task, isLoading, isError, error } = useQuery({
    queryKey: ['paperwork-admin-task', id],
    queryFn: async () => (await api.get(`/paperwork/admin/tasks/${id}`)).data?.data,
    enabled: !!id,
  });

  const { data: workLog, isLoading: logLoading } = useQuery({
    queryKey: ['paperwork-admin-task-log', id],
    queryFn: async () => (await api.get(`/paperwork/admin/tasks/${id}/log`)).data?.data,
    enabled: !!id && logOpen,
  });

  const confirmMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/paperwork/admin/tasks/${id}/confirm`, {
          note: note.trim(),
          document_id: docId.trim() ? Number(docId.trim()) : undefined,
          close: true,
        })
      ).data?.data,
    onSuccess: () => {
      toast.success('Confirmed — problem closed');
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-working'] });
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-overview'] });
      navigate('/paperwork/working');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Confirm failed'),
  });

  const cancelMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/paperwork/admin/tasks/${id}/release`, {
          note: 'Cancel — returned to Overview',
        })
      ).data?.data,
    onSuccess: () => {
      toast.success('Cancelled — back on Overview list');
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-working'] });
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-overview'] });
      const q = task?.queue_type ? `?queue=${encodeURIComponent(task.queue_type)}` : '';
      navigate(`/paperwork${q}`);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Cancel failed'),
  });

  const events = (task?.events || []).filter(
    (e) => e.event_type === 'note' || e.event_type === 'take' || e.event_type === 'cancel'
  );
  const recent = events.slice(0, 2);

  return (
    <div className="pb-28">
      <ModulePageHeading
        resolveNav={resolvePaperworkNav}
        title="Paperwork"
        showBack
      />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p className="text-sm text-red-600">{error?.response?.data?.error || 'Task not found'}</p>
      )}

      {task && (
        <div className="max-w-2xl space-y-4">
          <div className="rounded-xl border bg-white p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold ${queueBadgeClass(
                  task.queue_type
                )}`}
              >
                {task.queue_label || task.queue_type}
              </span>
              {task.status === 'closed' ? (
                <span className="text-xs font-semibold text-slate-500 uppercase">Closed</span>
              ) : null}
            </div>
            <p className="text-base font-semibold text-gray-900">
              {task.building_name || task.building_id}
              {task.ss_code ? (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => setLogOpen(true)}
                    className="text-primary-700 underline decoration-primary-300 hover:decoration-primary-600 font-semibold"
                    title="Open work log for this SS"
                  >
                    {formatSsLabel(task.ss_code)}
                  </button>
                </>
              ) : (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => setLogOpen(true)}
                    className="text-primary-700 underline decoration-primary-300 hover:decoration-primary-600 font-semibold text-sm"
                    title="Open work log for this building"
                  >
                    Work log
                  </button>
                </>
              )}
            </p>
            <p className="text-sm text-gray-800">{task.reason_text || 'Blocked paperwork item'}</p>
            <p className="text-xs text-gray-500">
              Next: {task.next_action || '—'}
              {task.days_stuck != null ? ` · ${task.days_stuck}d` : ''}
            </p>
            {task.assignee_name ? (
              <p className="text-sm font-semibold text-primary-800">Work on {task.assignee_name}</p>
            ) : null}

            {task.look_path ? (
              <Link
                to={task.look_path}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-slate-50 text-sm font-semibold text-primary-800 hover:bg-slate-100"
              >
                Open: building Paperwork <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            ) : null}
          </div>

          {recent.length > 0 && (
            <div className="rounded-xl border bg-white p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent</p>
                <button
                  type="button"
                  onClick={() => setLogOpen(true)}
                  className="text-xs font-semibold text-primary-700 hover:underline"
                >
                  Full work log
                </button>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-700">
                {recent.map((ev) => (
                  <li key={ev.id}>
                    {formatWhen(ev.created_at)}
                    {ev.note ? ` · ${ev.note}` : ` · ${eventLabel(ev.event_type)}`}
                    {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {task.status === 'open' && (
            <div className="rounded-xl border bg-white p-4 space-y-3">
              <label className="block text-sm font-medium text-gray-800">
                Memo (required to confirm)
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm min-h-[88px]"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What you did — call, chase, lodge, etc."
                />
              </label>
              <label className="block text-sm text-gray-700">
                Document id (optional — from building Documents)
                <input
                  type="text"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={docId}
                  onChange={(e) => setDocId(e.target.value)}
                  placeholder="e.g. 1234"
                />
              </label>
            </div>
          )}

          {task.status === 'closed' && task.close_note && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-950">
              Closed: {task.close_note}
            </div>
          )}
        </div>
      )}

      {/* Work log modal — SS click */}
      {logOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
          <div
            className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
            role="dialog"
            aria-labelledby="work-log-title"
          >
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
              <div className="min-w-0">
                <h3 id="work-log-title" className="font-semibold text-gray-900">
                  Work log
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {workLog?.building_name || task?.building_name}
                  {workLog?.ss_code || task?.ss_code
                    ? ` · ${formatSsLabel(workLog?.ss_code || task?.ss_code)}`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLogOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-800"
                aria-label="Close log"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {logLoading && <p className="text-sm text-gray-500">Loading log…</p>}
              {!logLoading && (
                <ul className="space-y-3 text-sm">
                  {(workLog?.events || []).map((ev) => (
                    <li key={`${ev.task_id}-${ev.id}`} className="border-b border-slate-100 pb-2">
                      <p className="text-xs text-gray-500">
                        {formatWhen(ev.created_at)}
                        {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                        {ev.queue_type ? ` · ${ev.queue_type}` : ''}
                      </p>
                      <p className="text-gray-900 font-medium">{eventLabel(ev.event_type)}</p>
                      {ev.note ? <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{ev.note}</p> : null}
                    </li>
                  ))}
                  {!workLog?.events?.length && (
                    <li className="text-gray-500 py-6 text-center">No log entries yet for this SS.</li>
                  )}
                </ul>
              )}
            </div>
            <div className="px-4 py-3 border-t">
              <button
                type="button"
                onClick={() => setLogOpen(false)}
                className="w-full px-3 py-2 rounded-lg border text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky: Back · Cancel (always on) · Confirm */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <div className="max-w-2xl mx-auto flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2.5 rounded-lg border bg-white text-sm font-semibold text-gray-800"
          >
            Back
          </button>
          {task?.status === 'open' && (
            <>
              <button
                type="button"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate()}
                className="px-4 py-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-950 text-sm font-semibold hover:bg-amber-100 disabled:opacity-60"
              >
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={confirmMutation.isPending || !note.trim()}
                onClick={() => confirmMutation.mutate()}
                className="px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-40"
              >
                {confirmMutation.isPending ? 'Submitting…' : 'Confirm'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
